import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat, fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import DButton from "discourse/ui-kit/d-button";
import DUserLink from "discourse/ui-kit/d-user-link";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import userDisplayName from "../lib/user-display-name";

export default class CommunityFeedbackTopicStatus extends Component {
  @service a11y;

  @tracked updating = false;
  @tracked feedbackOverride;

  get topic() {
    return this.args.outletArgs.model;
  }

  get feedback() {
    return this.feedbackOverride || this.topic.community_feedback;
  }

  get isWish() {
    return this.feedback?.item_type === "wish";
  }

  get nextFeedbackStatus() {
    return this.feedback?.status === "adopted" ? "collecting" : "adopted";
  }

  get claimantDisplayName() {
    return userDisplayName(this.feedback?.claimed_by);
  }

  updateFeedback(feedback, announcementKey) {
    this.feedbackOverride = feedback;
    this.topic.set("community_feedback", feedback);
    this.a11y.announce(i18n(announcementKey), "polite");
  }

  @action
  async claim() {
    this.updating = true;
    try {
      const feedback = await ajax(
        `/community-feedback/topics/${this.topic.id}/claim.json`,
        { type: "PUT" }
      );
      this.updateFeedback(feedback, "community_feedback.wishes.topic.claimed");
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.updating = false;
    }
  }

  @action
  async unclaim() {
    this.updating = true;
    try {
      const feedback = await ajax(
        `/community-feedback/topics/${this.topic.id}/claim.json`,
        { type: "DELETE" }
      );
      this.updateFeedback(feedback, "community_feedback.wishes.topic.unclaimed");
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.updating = false;
    }
  }

  @action
  async changeStatus(status) {
    this.updating = true;
    try {
      const feedback = await ajax(
        `/community-feedback/topics/${this.topic.id}/status.json`,
        { type: "PUT", data: { status } }
      );
      this.updateFeedback(
        feedback,
        this.isWish
          ? "community_feedback.wishes.topic.status_updated"
          : "community_feedback.topic.status_updated"
      );
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.updating = false;
    }
  }

  <template>
    {{#if this.feedback}}
      <div
        class={{dConcatClass
          "community-feedback-topic-status"
          (if this.isWish "--wish")
        }}
        ...attributes
      >
        <span
          class={{dConcatClass
            "community-feedback-topic-status__badge"
            (concat "--" this.feedback.status)
          }}
        >
          {{i18n
            (concat "community_feedback.statuses." this.feedback.status)
          }}
        </span>
        <span class="community-feedback-topic-status__game">
          {{this.feedback.game_name}}
        </span>
        {{#if this.feedback.claimed_by}}
          <DUserLink
            @user={{this.feedback.claimed_by}}
            class="community-feedback-topic-status__claimant"
          >
            {{i18n
              "community_feedback.wishes.claimed_by"
              username=this.claimantDisplayName
            }}
          </DUserLink>
        {{/if}}
        {{#if this.isWish}}
          {{#if this.feedback.can_claim}}
            <DButton
              class="btn-primary btn-small community-feedback-topic-status__action"
              @action={{this.claim}}
              @isLoading={{this.updating}}
              @disabled={{this.updating}}
              @label="community_feedback.wishes.topic.claim"
            />
          {{/if}}
          {{#if this.feedback.can_unclaim}}
            <DButton
              class="btn-default btn-small community-feedback-topic-status__action"
              @action={{this.unclaim}}
              @isLoading={{this.updating}}
              @disabled={{this.updating}}
              @label="community_feedback.wishes.topic.unclaim"
            />
          {{/if}}
          {{#each this.feedback.allowed_statuses as |status|}}
            <DButton
              class="btn-default btn-small community-feedback-topic-status__action"
              @action={{fn this.changeStatus status}}
              @isLoading={{this.updating}}
              @disabled={{this.updating}}
              @translatedLabel={{i18n
                (concat "community_feedback.wishes.topic.change_to." status)
              }}
            />
          {{/each}}
        {{else if this.feedback.can_change_status}}
          <DButton
            class="btn-default btn-small community-feedback-topic-status__action"
            @action={{fn this.changeStatus this.nextFeedbackStatus}}
            @isLoading={{this.updating}}
            @disabled={{this.updating}}
            @translatedLabel={{i18n
              (concat
                "community_feedback.topic.change_to."
                this.nextFeedbackStatus
              )
            }}
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
